import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '~/lib/api-client';
import { operatorKeys } from '~/lib/query-keys';
import { describeOperatorMutationError } from './describe-operator-mutation-error';
import { OPERATOR_HAND_BACK_MUTATION_KEY } from '~/features/operator/use-operator-takeover-mutations-in-flight';
import type { OperatorRunWire } from '~/hooks/queries/use-operator-runs';

/**
 * FRONT-1996 "Take the wheel": the "Hand back" button, in both the chat
 * card and the operator panel header. `POST /api/operator/runs/:runId/
 * hand-back` is owner-scoped and idempotent — nothing left to end is a
 * plain 200 with the run's current wire, never an error (per
 * `src/operator/routes.ts`) — the only failure this route can answer at
 * all is a 404 for a run that isn't the caller's own, so unlike a stop
 * request there is no 409-shaped "already handled" race to special-case
 * here. Still routed through the same `describeOperatorMutationError`
 * map/invalidate/cancel treatment as `useTakeOverOperatorRun` (reviews
 * M6/M7/M8) for whatever DOES fail — network errors, a 404, a future 409 —
 * rather than assuming today's "it never 409s" holds forever.
 */
export function useHandBackOperatorRun() {
  const qc = useQueryClient();
  return useMutation({
    // FRONT-1996 review M7: shared across every caller (card + panel) —
    // see useTakeOverOperatorRun's own comment on the matching key.
    mutationKey: OPERATOR_HAND_BACK_MUTATION_KEY,
    mutationFn: ({ runId }: { runId: string }) =>
      api.post<OperatorRunWire>(`/api/operator/runs/${encodeURIComponent(runId)}/hand-back`, {}),
    // FRONT-1996 review M8: see useTakeOverOperatorRun's own comment —
    // identical race, identical fix.
    onMutate: async ({ runId }) => {
      await qc.cancelQueries({ queryKey: operatorKeys.run(runId) });
    },
    onSuccess: (run) => {
      qc.setQueryData(operatorKeys.run(run.id), run);
    },
    onError: (err, { runId }) => {
      toast.error(`Hand back failed: ${describeOperatorMutationError(err)}`);
      qc.invalidateQueries({ queryKey: operatorKeys.run(runId) });
    },
  });
}
